import { initializeApp, getApps } from 'firebase/app'
import {
  getFunctions,
  connectFunctionsEmulator,
  Functions,
} from 'firebase/functions'

import {
  FunctionsRouting,
  QuickPassFirebaseConfig,
} from '../_types/firebase-config'

const DEFAULT_FUNCTIONS_REGION = 'northamerica-northeast1'

let cachedFunctions: Functions | null = null

/**
 * Reads the Firebase web SDK config from Vite environment variables. Pat
 * fills these in from the shared QuickCasa Firebase project's web app
 * settings (Firebase Console → Project settings → Your apps → SDK setup).
 *
 * @returns {QuickPassFirebaseConfig}
 */
const readFirebaseConfigFromEnvironment = (): QuickPassFirebaseConfig => {
  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET ?? '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID ?? '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
  }
}

/**
 * Parses `VITE_API_URL` into the function-name + region + optional
 * emulator host/port that the Firebase Functions SDK needs to talk to
 * api-server-gateway. Supports both:
 *
 *   Emulator format: http://host:port/<projectId>/<region>/<functionName>
 *   Cloud Functions: https://<region>-<projectId>.cloudfunctions.net/<functionName>
 *
 * For Cloud Run URLs (https://<funcName>-<hash>.a.run.app), parsing the
 * function name reliably is impossible without the project hash, so callers
 * should set `VITE_API_FUNCTION_NAME` and `VITE_FUNCTIONS_REGION` directly.
 *
 * @returns {FunctionsRouting | null}
 */
const parseFunctionsRoutingFromEnvironment = (): FunctionsRouting | null => {
  const explicitFunctionName = import.meta.env.VITE_API_FUNCTION_NAME ?? ''
  const explicitRegion =
    import.meta.env.VITE_FUNCTIONS_REGION ?? DEFAULT_FUNCTIONS_REGION

  if (explicitFunctionName !== '') {
    return {
      region: explicitRegion,
      functionName: explicitFunctionName,
      emulatorHost: null,
      emulatorPort: null,
    }
  }

  const apiUrl = import.meta.env.VITE_API_URL ?? ''

  if (apiUrl === '') {
    return null
  }

  let parsed: URL
  try {
    parsed = new URL(apiUrl)
  } catch (error) {
    console.warn('VITE_API_URL is not a valid URL', error)
    return null
  }

  const pathSegments = parsed.pathname.split('/').filter(part => part !== '')

  const isEmulatorHost =
    parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1'

  if (isEmulatorHost === true && pathSegments.length >= 3) {
    const port = Number.parseInt(parsed.port, 10)

    return {
      region: pathSegments[1],
      functionName: pathSegments[2],
      emulatorHost: parsed.hostname,
      emulatorPort: Number.isNaN(port) ? 5001 : port,
    }
  }

  if (
    parsed.hostname.endsWith('.cloudfunctions.net') &&
    pathSegments.length >= 1
  ) {
    const regionFromHostname = parsed.hostname.split('-')[0]

    return {
      region: regionFromHostname,
      functionName: pathSegments[pathSegments.length - 1],
      emulatorHost: null,
      emulatorPort: null,
    }
  }

  return null
}

/**
 * Initialises the Firebase web SDK exactly once. Idempotent — safe to call
 * multiple times (subsequent calls short-circuit). Must run before any
 * Firebase Auth or Firestore call.
 *
 * @returns {void}
 */
const initializeFirebase = (): void => {
  if (getApps().length > 0) {
    return
  }

  const config = readFirebaseConfigFromEnvironment()

  if (config.apiKey === '') {
    console.warn(
      'Firebase config is missing — copy .env.example to .env.local and fill in the VITE_FIREBASE_* variables before signing in.',
    )
  }

  initializeApp(config)
}

/**
 * Returns the configured Firebase Functions instance, connecting the
 * emulator on first call when `VITE_API_URL` points at localhost. Cached
 * so subsequent calls reuse the same instance.
 *
 * @returns {Functions | null}
 */
const getQuickPassFunctions = (): Functions | null => {
  if (cachedFunctions !== null) {
    return cachedFunctions
  }

  const routing = parseFunctionsRoutingFromEnvironment()

  if (routing === null) {
    return null
  }

  const functions = getFunctions(undefined, routing.region)

  if (routing.emulatorHost !== null && routing.emulatorPort !== null) {
    connectFunctionsEmulator(
      functions,
      routing.emulatorHost,
      routing.emulatorPort,
    )
  }

  cachedFunctions = functions
  return functions
}

/**
 * Returns the function name configured for api-server-gateway calls.
 *
 * @returns {string | null}
 */
const getQuickPassFunctionName = (): string | null => {
  const routing = parseFunctionsRoutingFromEnvironment()

  if (routing === null) {
    return null
  }

  return routing.functionName
}

export { initializeFirebase, getQuickPassFunctions, getQuickPassFunctionName }
